// Kundenangaben zum Bedarf lesen und schreiben — eine Zeile je Kunde und Artikel.
//
// Gelesen wird sie vom Bedarfs-Tab (als dritte Quelle neben Auftraegen und
// Lieferungen, siehe recommendationOf), geschrieben an drei Stellen: von Hand im
// Tab, aus einer Aktivitaet heraus ueber die KI-Auswertung, und beim Loeschen einer
// Forecast-Aufgabe — dort als Sperre statt als Angabe.
import { client } from "./amplifyClient";
import {
  demandInputsByCompany,
  createDemandInputs,
  updateDemandInputs,
} from "../components/demandPlanning/graphql/demandInputs.queries";

const DAY = 24 * 60 * 60 * 1000;

// objectType der Aufgaben, die der naechtliche Lauf anlegt. Auch das Frontend
// braucht ihn: daran erkennt `deleteTask`, dass ein Loeschen eine Sperre verdient.
export const DEMAND_TASK_TYPE = "demandForecast";

// Wohin eine Forecast-Aufgabe zeigt: auf den Bedarfs-Tab ihres Kunden. Sie deckt
// ALLE faelligen Artikel dieses Kunden ab, es gibt also keinen einzelnen, auf den
// sie zeigen koennte — und dort steht genau die Liste, die auch im Aufgabentext
// steht, nur mit Zahlen und Zeitachse. Der Aktivitaetenverlauf, auf dem die
// Firmenseite sonst landet, ist der falsche Ort.
//
// Das objectID der Aufgabe ist die reine companyId. Getrennt von `objectRoute` in
// components/activityLinks.js gehalten, damit dieses Wissen an einer Stelle liegt
// und nicht in der generischen Weiche fuer Auftraege und Angebote.
export const demandTaskRoute = (companyId) =>
  companyId
    ? `/customer/${companyId}?tab=${encodeURIComponent("Demand Planning")}`
    : null;

// Artikelnummer -> Zeile. Leeres Objekt, wenn es zu diesem Kunden nichts gibt.
export const loadDemandInputs = async (companyId) => {
  if (!companyId) return {};
  const byItem = {};
  let nextToken = null;
  do {
    const response = await client.graphql({
      query: demandInputsByCompany,
      variables: { companyId, limit: 200, nextToken },
    });
    const page = response.data?.demandInputsByCompanyIdByItemNumber;
    (page?.items ?? []).forEach((row) => {
      byItem[row.itemNumber] = row;
    });
    nextToken = page?.nextToken ?? null;
  } while (nextToken);
  return byItem;
};

// Die Felder der Kundenangabe selbst, im Unterschied zu Sperre und Notiz.
const INPUT_FIELDS = [
  "stockQuantity",
  "stockDate",
  "plannedOrderDate",
  "plannedQuantity",
  "neededByDate",
];

// Anlegen oder ueberschreiben. `existing` ist die schon geladene Zeile, falls es
// eine gibt — dann bleibt es bei einem Update und die id des Aufrufers gilt weiter.
export const saveDemandInput = async ({ companyId, itemNumber, existing, ...changes }) => {
  const query = existing?.id ? updateDemandInputs : createDemandInputs;
  // `createdByUserId` gehoert zum Anlegen. Die Aufrufer schicken es bei jedem
  // Schreiben mit — auch beim Klick auf „nicht erinnern" —, und ungefiltert stuende
  // darin am Ende, wer die Zeile ZULETZT angefasst hat. Genau die Auskunft, fuer
  // die es das Feld nicht gibt.
  const { createdByUserId, ...rest } = changes;
  // Wann die KUNDENANGABE zuletzt gepflegt wurde — nur wenn sie mitgeschrieben
  // wird. `updatedAt` taugt dafuer nicht: das rueckt auch beim Setzen oder
  // Aufheben einer Erinnerungssperre vor, und eine laengst ueberholte Bestandszahl
  // galte danach wieder als neuer als die letzte Bestellung (recommendationOf).
  if (INPUT_FIELDS.some((field) => field in rest))
    rest.recordedAt = new Date().toISOString();
  const input = existing?.id
    ? { id: existing.id, ...rest }
    : { companyId, itemNumber, createdByUserId, ...rest };
  const response = await client.graphql({ query, variables: { input } });
  const saved = response.data?.updateDemandInputs ?? response.data?.createDemandInputs;
  // Der Client wirft bei GraphQL-Fehlern nicht, er gibt sie zurueck. Ohne diese
  // Pruefung meldeten die Aufrufer „Gespeichert", obwohl nichts gespeichert wurde.
  if (!saved) throw new Error(JSON.stringify(response.errors ?? "no row returned"));
  return saved;
};

// Ruhe fuer EINEN Artikel, gesetzt beim Loeschen oder Erledigen seiner Aufgabe und
// von Hand im Bedarfs-Tab.
// ponytail: ein Monat, dieselbe Spanne wie die Kundenpause. Genauer waere der echte
// Rhythmus des Artikels, den nur der naechtliche Lauf kennt — er muesste ihn dann an
// der Aufgabe hinterlegen. Lohnt nicht: bestellt der Kunde, hebt `recommendationOf`
// die Sperre ohnehin auf, und bestellt er nicht, holt der naechste Lauf sie zurueck.
// Lieber einmal zu frueh nachfassen als einen Abruf verpassen — ein Quartal Ruhe
// deckt bei einem Vierteljahresartikel genau den Zyklus zu, um den es ging.
export const DEMAND_ITEM_SUPPRESS_DAYS = 30;

// Pause fuer den GANZEN Kunden, wenn der Nutzer sie beim Loeschen anhakt.
export const COMPANY_PAUSE_DAYS = 30;

// Ruhe nach JEDEM Loeschen einer Bedarfsaufgabe. Dieselbe Spanne wie COOL_OFF_DAYS
// im Nachtlauf (lineupdashboardDemandContactScheduled) nach dem Erledigen.
export const DEMAND_DELETE_PAUSE_DAYS = 7;

// Legt der naechtliche Lauf ueberhaupt Bedarfsaufgaben an? Vorerst nein — erst wird
// auf productive geprueft, ob die Prognosen stimmen. Nur fuer die Hinweise im
// Bedarfs-Tab; der eigentliche Schalter ist TASKS_ENABLED in
// lineupdashboardDemandContactScheduled/src/index.js. Beide zusammen umstellen.
export const DEMAND_AUTO_TASKS_ENABLED = false;

// Ab wievielen Bestellterminen der naechtliche Lauf aus einem fortgeschriebenen
// Rhythmus eine Aufgabe macht. Muss mit MIN_ORDERS_FOR_TASK in
// lineupdashboardDemandContactScheduled uebereinstimmen.
//
// Vier Termine sind drei Abstaende — erst ab dreien misst die XYZ-Einstufung
// ueberhaupt etwas (STABILITY_MIN_GAPS in orderHistory.js), und nur dann greift die
// Z-Sperre, die sporadische Besteller heraushaelt. Bei zwei Terminen gibt es einen
// einzigen Abstand; daraus wird ein Median, ein Termin und eine Aufgabe mit einer
// Genauigkeit, die nirgends gemessen ist.
// Gilt NUR fuer die Fortschreibung: hat der Kunde selbst einen Termin oder seinen
// Bestand genannt, braucht es keine Statistik, die das stuetzt.
// Keine Mindestmenge und kein Mindestumsatz — ein Anruf lohnt sich bei jeder Menge.
export const MIN_ORDERS_FOR_TASK = 4;

// Eine kundenweite Pause passt nicht in eine Tabelle mit einer Zeile je Kunde UND
// Artikel — und Artikel, zu denen nie jemand etwas erfasst hat, haetten gar keine
// Zeile, in der sie stehen koennte. Also eine Sentinel-Zeile: derselbe Kunde, als
// Artikelnummer ein Stern.
// "*" kann keine echte Artikelnummer sein (die sind sechsstellig numerisch, siehe
// isCostPosition in orderHistory.js), die Zeile kollidiert also mit keinem Artikel
// und faellt in `inputs[artikelnummer]` nie jemandem in die Haende.
export const DEMAND_COMPANY_KEY = "*";

// Erinnerungen fuer den ganzen Kunden pausieren — oder mit `days: null` wieder
// einschalten. Eine laufende laengere Pause wird nie verkuerzt: wer den Kunden
// einen Monat stillgestellt hat und dann eine Aufgabe loescht, behaelt den Monat.
export const pauseDemandRemindersForCompany = async (
  companyId,
  days = COMPANY_PAUSE_DAYS
) => {
  if (!companyId) return null;
  const existing = (await loadDemandInputs(companyId))[DEMAND_COMPANY_KEY];
  const until = days ? Date.now() + days * DAY : null;
  if (until && (pauseUntilOf(existing) ?? 0) >= until) return existing;
  return saveDemandInput({
    companyId,
    itemNumber: DEMAND_COMPANY_KEY,
    existing,
    suppressUntil: until ? new Date(until).toISOString() : null,
    suppressReason: days ? "company" : null,
  });
};

// Laeuft auf dieser Zeile gerade eine Pause? Millisekunden oder null — eine
// abgelaufene ist keine.
export const pauseUntilOf = (row) => {
  const ms = row?.suppressUntil ? new Date(row.suppressUntil).getTime() : NaN;
  return Number.isNaN(ms) || ms <= Date.now() ? null : ms;
};

// Dieselbe Frage fuer den ganzen Kunden. Erwartet die Map aus `loadDemandInputs`,
// damit der Aufrufer nicht ein zweites Mal laedt.
export const companyPauseUntil = (inputs) =>
  pauseUntilOf(inputs?.[DEMAND_COMPANY_KEY]);

// Eine artikelgenaue Sperre beim Loeschen gibt es bewusst NICHT mehr: die Aufgabe
// deckt alle faelligen Artikel des Kunden ab, und welcher davon der Grund fuers
// Wegklicken war, weiss nur der Nutzer. Einen zu raten hiesse, ihn einen Monat lang
// lautlos zu verstecken. Gefragt wird stattdessen nach dem ganzen Kunden
// (pauseDemandRemindersForCompany); einen einzelnen Artikel stellt man im
// Bedarfs-Tab still, wo man sieht, was man tut.
